import { Anchor, Box, Stack, Text, Title } from "@mantine/core";
import { Link } from "@/i18n/navigation";

export default function UndertoneChecks() {
  return (
    <Stack component="section" gap="md" mt="xl" aria-labelledby="undertone-tests-disagree">
      <Title order={2} size="h3" id="undertone-tests-disagree">
        What is my undertone if the tests disagree?
      </Title>
      <Text c="dimmed">
        Blue-green veins, a preference for gold jewelry, and a cool quiz result can leave you with
        mixed signals. That is a reason to check again under consistent conditions. It does not
        automatically mean you have a neutral undertone. Use the quiz as a starting point, then
        compare how warm and cool colors look beside your face.
      </Text>
      <Box>
        <Title order={3} size="h4" mb="sm">Try a more consistent warm vs. cool comparison</Title>
        <Box component="ol" pl="lg" m={0}>
          <Box component="li" mb="sm">
            <strong>Keep the lighting the same.</strong> Face a window in indirect daylight,
            turn off colored room lights, and avoid direct sun. If possible, compare without
            tinted makeup and keep brightly colored clothing away from your face.
          </Box>
          <Box component="li" mb="sm">
            <strong>Change one thing at a time.</strong> Hold a warm coral and a cool pink fabric
            below your chin, using shades of similar brightness. Look for a repeatable preference
            in the appearance of your skin, rather than choosing your favorite fabric color.
          </Box>
          <Box component="li" mb="sm">
            <strong>Repeat with another pair.</strong> Try cream and white in the same position.
            If your preference changes between pairs or lighting conditions, keep your result
            tentative instead of forcing a warm or cool answer.
          </Box>
        </Box>
        <Text size="sm" mt="sm" c="dimmed">
          No fabrics handy? <Anchor component={Link} href="/virtual-color-draping">Try virtual color draping</Anchor>
          {" "}to compare colors around the same photo. Digital drapes are a visual aid; your camera
          and screen can change the colors you see.
        </Text>
      </Box>
      <Box>
        <Title order={3} size="h4" mb="sm">Does a neutral result mean I can wear every color?</Title>
        <Text c="dimmed">
          A neutral result means this quiz did not identify a strong warm or cool preference from
          your answers. It is not a measurement of your skin. You may still prefer lighter,
          deeper, brighter, or more muted colors. Our <Anchor component={Link} href="/neutral-undertones-what-colors-suit-you">neutral undertone color guide</Anchor>
          {" "}gives you a starting palette; the <Anchor component={Link} href="/color-seasons-chart">color seasons chart</Anchor>
          {" "}shows how depth and brightness help narrow the options further.
        </Text>
      </Box>
      <Box>
        <Title order={3} size="h4" mb="sm">Can a photo or AI tell me my undertone?</Title>
        <Text c="dimmed">
          A photo-based analysis can suggest a direction, but lighting, filters, makeup, and camera
          white balance can affect the result. This free quiz uses your answers, not a photo scan.
          If you try a photo analysis afterward, use an unfiltered daylight photo and compare the
          suggested colors in person before changing your makeup or wardrobe.
        </Text>
      </Box>
    </Stack>
  );
}
